import React from 'react';

// Styles & Assets
import styles from './LoginPage.module.css';
import logo from './../../assets/common/logo.svg';

// Services
import { validateNonEmpty } from '../../services/General';
import { getEnvironmentApiUrl } from '../../services/General';

// Hooks
import useInput from '../../hooks/useInput';
import useHttp from '../../hooks/useHttp';

// Components
import Input from '../../components/UI/Input/Input';
import Spinner from '../../components/UI/Spinner/Spinner';

// Interfaces
import { DefaultLogin } from '../../interfaces/UserInfo';

// Language
import { useTranslation } from 'react-i18next';

// Other
import { useNavigate, useSearchParams } from 'react-router-dom';

// LoginPage: page that renders a login form and validates the user when submitted.
// the GenerateRoute component. If user credentials are correct user info and and the
// token expiration time are saved in the localstorage and we navigate to ValidateLoginPage
// sending the token.
const LoginPage: React.FC = () => {

    const { t } = useTranslation();
    const navigate = useNavigate();
    
    const [searchParams] = useSearchParams();
    const redirectTo = searchParams.get('redirectTo');
    const navigatePath = redirectTo ? `/validatelogin?redirectTo=${encodeURIComponent(redirectTo)}` : '/validatelogin';

    const { isLoading, error, sendRequest: sendCredentials } = useHttp<DefaultLogin>();

    const usernameInput =
        useInput(validateNonEmpty, undefined, t('mail_required') as string);

    const passwordInput =
        useInput(validateNonEmpty, undefined, t('password_required') as string);

    const transformData = (request: DefaultLogin) => {
        localStorage.setItem('user', JSON.stringify(request.user));
        localStorage.setItem('refresh-token', request.refreshToken);
        localStorage.setItem('firebase-token', request.firebase_token);
        localStorage.setItem('refresh-token-expiration-date', request.refreshTokenExpTime);

        navigate(navigatePath, { state: { token: request.authToken, firebaseToken: request.firebase_token } });
    };

    const submitFormLogin = (evt: React.FormEvent) => {
        evt.preventDefault();
        if (!isFormValid) return;
        sendCredentials({
            url: getEnvironmentApiUrl() + 'login/default',
            method: 'POST',
            headers: { 'Content-Type': 'application/json', api_key: 'digitomedica' },
            body: {
                application: 'bc_home',
                use_new_service: true,
                user: usernameInput.value,
                password: passwordInput.value
            },
        }, transformData);
    };

    const isFormValid = usernameInput.valueIsValid && passwordInput.valueIsValid;
    const buttonClasses = isFormValid ? styles.loginButton : styles.loginButton + ' ' + styles.buttonDisabled;

    return (
        <div className={styles.loginWrapper}>
            <main className={styles.formSignIn} >
                <img
                    className={styles.logoHospital}
                    src={window.config.USE_HOSPITAL_LOGO ? '/images/logo_hospital.png' : logo}
                    onError={(e) => e.currentTarget.src = logo}
                    alt='hospital logo'
                />
                <div className={styles.loginPanel}>
                    <form noValidate autoComplete='false' onSubmit={submitFormLogin} className={styles.form}>
                        <Input
                            label={t('user')}
                            defaultValue={usernameInput.value}
                            error={usernameInput.hasError}
                            errorMsg={usernameInput.errorMsg}
                            onChange={usernameInput.valueChangeHandler}
                            onBlur={usernameInput.inputBlurHandler}
                        />
                        <Input
                            label={t('password')}
                            defaultValue={passwordInput.value}
                            error={passwordInput.hasError}
                            errorMsg={passwordInput.errorMsg}
                            onChange={passwordInput.valueChangeHandler}
                            onBlur={passwordInput.inputBlurHandler}
                            type='password'
                        />
                        {error &&
                            <div className='alertError'>
                                <p>{t('incorrect_user_pwd')}</p>
                            </div>
                        }
                        <button disabled={!isFormValid} className={buttonClasses} type="submit">
                            {isLoading && <Spinner color='#fff' size={32} fit />}
                            <span>{t('Login')}</span>
                        </button>
                    </form>
                </div>
            </main >
        </div>
    );
};
export default LoginPage;